import type { InsightsHeatmap } from './insights.js';

/**
 * Criterion trajectories: how each criterion's pass rate moved report over report.
 *
 * The average-score trend answers "are we getting better"; nothing answers "did the thing we
 * fixed last month stay fixed", which is the question a team asks of its own previous round of
 * notes. In single-market scope the heatmap is already a criterion × report matrix ordered
 * oldest to newest, so the same payload read along its rows is a per-criterion time series —
 * no new endpoint, no re-fetch.
 */

/** A trajectory needs two observations; one report is a snapshot. */
export const MIN_TREND_COLUMNS = 2;

/**
 * Only the most recent reports are plotted. Beyond this the dots crowd into a smudge, and a
 * criterion's recent history is what a producer acts on.
 */
export const MAX_TREND_COLUMNS = 8;

/**
 * Pass rates move in coarse steps on small reports — one creative flipping in a batch of twenty
 * is five points — so a move smaller than that reads as flat rather than as a direction.
 */
export const FLAT_TREND_DELTA = 5;

export type CriterionTrendDirection = 'IMPROVED' | 'REGRESSED' | 'FLAT';

/** One criterion's standing in one report. */
export interface CriterionTrendPoint {
    /** The report this point came from — the drill-through target. */
    columnId: string;
    label: string;
    /** Percentage of verdicts that passed, partials at half credit; null when none landed. */
    passRate: number | null;
    creativeCount: number;
}

export interface CriterionTrend {
    key: string;
    name: string;
    definition: string | null;
    weight: number;
    /** Oldest to newest, one per plotted report, including the gaps. */
    points: CriterionTrendPoint[];
    /** First observed pass rate in the window. */
    first: number;
    /** Most recent observed pass rate. */
    latest: number;
    /** `latest - first`, in percentage points. */
    delta: number;
    direction: CriterionTrendDirection;
}

function passRateOf(missRate: number | null): number | null {
    return missRate == null ? null : Math.round((1 - missRate) * 100);
}

function directionOf(delta: number): CriterionTrendDirection {
    if (Math.abs(delta) < FLAT_TREND_DELTA) return 'FLAT';
    return delta > 0 ? 'IMPROVED' : 'REGRESSED';
}

/**
 * Per-criterion pass-rate trajectories over the most recent reports, biggest mover first.
 *
 * Returns nothing unless the heatmap is pivoted by report — market columns have no order to
 * read as time. A criterion observed in fewer than two of the plotted reports is left out
 * rather than drawn as a single dot.
 */
export function buildCriterionTrends(heatmap: InsightsHeatmap): CriterionTrend[] {
    if (heatmap.columnKind !== 'REPORT') return [];
    if (heatmap.columns.length < MIN_TREND_COLUMNS) return [];

    const offset = Math.max(0, heatmap.columns.length - MAX_TREND_COLUMNS);
    const columns = heatmap.columns.slice(offset);

    const trends: CriterionTrend[] = [];

    for (const row of heatmap.rows) {
        const points = columns.map((column, index): CriterionTrendPoint => ({
            columnId: column.id,
            label: column.label,
            passRate: passRateOf(row.cells[offset + index]?.missRate ?? null),
            creativeCount: column.creativeCount,
        }));

        const observed = points
            .map((point) => point.passRate)
            .filter((rate): rate is number => rate != null);
        if (observed.length < MIN_TREND_COLUMNS) continue;

        const first = observed[0]!;
        const latest = observed[observed.length - 1]!;
        const delta = latest - first;

        trends.push({
            key: row.key,
            name: row.name,
            definition: row.definition,
            weight: row.weight,
            points,
            first,
            latest,
            delta,
            direction: directionOf(delta),
        });
    }

    // Biggest movers first — the point of the view is what changed. A regression outranks an
    // equal-sized improvement: it is the one that needs someone to look at it.
    return trends.sort(
        (a, b) =>
            Math.abs(b.delta) - Math.abs(a.delta) ||
            a.delta - b.delta ||
            b.weight - a.weight ||
            a.name.localeCompare(b.name),
    );
}
